import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function AvailableOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unsettledCodInfo, setUnsettledCodInfo] = useState(null);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await deliveryAPI.getAvailableOrders();
      setOrders(response.data.data.orders);

      // Store unsettled COD information
      if (response.data.data.hasUnsettledCod) {
        setUnsettledCodInfo({
          count: response.data.data.unsettledCodCount,
          orders: response.data.data.unsettledCodOrders
        });
      } else {
        setUnsettledCodInfo(null);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      await deliveryAPI.acceptOrder(orderId);
      alert('Order accepted! Navigate to My Deliveries to manage it.');
      fetchOrders();
    } catch (error) {
      alert('Failed to accept order: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) return <div className="loading">🔄 Loading available orders...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            📦 Available Orders
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Accept orders to start earning • Auto-refreshes every 10 seconds
          </p>
        </div>

        {/* Unsettled COD Warning Banner */}
        {unsettledCodInfo && (
          <div className="card fade-in" style={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #e85a5a 100%)',
            color: 'white',
            marginBottom: 'var(--space-2xl)',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(255, 107, 107, 0.4)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-lg)' }}>
              <div style={{ fontSize: '60px' }}>⚠️</div>
              <div style={{ flex: 1 }}>
                <h2 style={{
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '800',
                  marginBottom: 'var(--space-md)',
                  color: 'white'
                }}>
                  COD Payment Settlement Required
                </h2>
                <p style={{
                  fontSize: 'var(--font-size-lg)',
                  marginBottom: 'var(--space-md)',
                  color: 'rgba(255, 255, 255, 0.95)'
                }}>
                  You have {unsettledCodInfo.count} unsettled COD payment{unsettledCodInfo.count > 1 ? 's' : ''}.
                  You must settle all pending COD payments before accepting new orders.
                </p>
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  {unsettledCodInfo.orders.map(order => (
                    <div key={order._id} style={{
                      padding: 'var(--space-sm)',
                      background: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: 'var(--radius-md)',
                      marginBottom: 'var(--space-sm)',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      Order #{order.orderNumber} - ₹{order.amount}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/delivery/my-deliveries')}
                  style={{
                    background: 'white',
                    color: '#ff6b6b',
                    padding: 'var(--space-md) var(--space-xl)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontWeight: '700',
                    fontSize: 'var(--font-size-base)',
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }}
                >
                  💰 Go to My Deliveries to Settle Payments
                </button>
              </div>
            </div>
          </div>
        )}

        {orders.length === 0 ? (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '120px', marginBottom: 'var(--space-lg)' }}>📦</div>
            <h2 style={{
              fontSize: 'var(--font-size-3xl)',
              fontWeight: '700',
              color: 'var(--gray-700)',
              marginBottom: 'var(--space-md)'
            }}>
              No available orders
            </h2>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
              Check back soon for new delivery requests
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-xl)' }}>
            {orders.map(order => (
              <div key={order._id} className="card fade-in" style={{
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Order Header */}
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <h2 style={{
                    fontSize: 'var(--font-size-3xl)',
                    fontWeight: '800',
                    color: 'var(--gray-900)',
                    marginBottom: 'var(--space-xs)'
                  }}>
                    Order #{order.orderNumber}
                  </h2>
                </div>

                {/* Pickup & Delivery Info */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--space-lg)',
                  marginBottom: 'var(--space-lg)',
                  paddingBottom: 'var(--space-lg)',
                  borderBottom: '2px solid var(--gray-100)'
                }}>
                  {/* Pickup Location */}
                  <div style={{
                    padding: 'var(--space-lg)',
                    background: 'rgba(255, 107, 107, 0.1)',
                    borderRadius: 'var(--radius-lg)',
                    border: '2px solid rgba(255, 107, 107, 0.2)'
                  }}>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-md)',
                      color: 'var(--gray-900)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-sm)'
                    }}>
                      <span>🏪</span>
                      <span>Pickup From</span>
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      color: 'var(--primary)',
                      marginBottom: 'var(--space-sm)'
                    }}>
                      {order.restaurantId?.name || 'Unknown Restaurant'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-700)',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      📍 {order.restaurantId?.address?.street || 'N/A'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-700)'
                    }}>
                      {order.restaurantId?.address?.city || 'N/A'}
                    </p>
                  </div>

                  {/* Delivery Location */}
                  <div style={{
                    padding: 'var(--space-lg)',
                    background: 'rgba(78, 205, 196, 0.1)',
                    borderRadius: 'var(--radius-lg)',
                    border: '2px solid rgba(78, 205, 196, 0.2)'
                  }}>
                    <h3 style={{
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-md)',
                      color: 'var(--gray-900)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-sm)'
                    }}>
                      <span>🏠</span>
                      <span>Deliver To</span>
                    </h3>
                    <p style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      color: 'var(--secondary)',
                      marginBottom: 'var(--space-sm)'
                    }}>
                      {order.userInfo?.name || 'Unknown User'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-700)',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      📍 {order.deliveryAddress?.street || 'N/A'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-700)',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      {order.deliveryAddress?.city || 'N/A'}
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-700)'
                    }}>
                      📱 {order.userInfo?.phone || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Order Details */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 'var(--space-md)',
                  marginBottom: 'var(--space-xl)'
                }}>
                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(255, 184, 77, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(255, 184, 77, 0.2)',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      ORDER TOTAL
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-2xl)',
                      fontWeight: '800',
                      color: 'var(--warning)',
                      margin: 0
                    }}>
                      ₹{order.pricing.total}
                    </p>
                  </div>

                  <div style={{
                    padding: 'var(--space-md)',
                    background: 'rgba(78, 205, 196, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid rgba(78, 205, 196, 0.2)',
                    textAlign: 'center'
                  }}>
                    <p style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--gray-600)',
                      marginBottom: 'var(--space-xs)',
                      fontWeight: '600'
                    }}>
                      PAYMENT
                    </p>
                    <p style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: '700',
                      color: 'var(--secondary)',
                      margin: 0,
                      textTransform: 'uppercase'
                    }}>
                      💳 {order.paymentMethod}
                    </p>
                  </div>
                </div>

                {/* Accept Button */}
                <button
                  onClick={() => acceptOrder(order._id)}
                  className="btn btn-success btn-lg"
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-sm)'
                  }}
                >
                  <span>✅</span>
                  <span>Accept This Delivery</span>
                </button>

                {/* Hover Accent */}
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: '150px',
                  height: '150px',
                  background: 'radial-gradient(circle at bottom right, rgba(78, 205, 196, 0.1) 0%, transparent 70%)',
                  pointerEvents: 'none'
                }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AvailableOrders;
